import type { Answer, DeliveryBoard, DeliveryRow, UnpairedAnswer } from "../../api";
import { formatAmount } from "../../format";

// The board's rows regrouped as CASES: every delivery and every set of receipts
// that the reconciliation ties together, as one thing to look at. A delivery
// and its receipts; a delivery with two sets (a possible double); three
// deliveries and the three sets that fit all of them (one ambiguous group, not
// three rows that each repeat the other two); a set of receipts with no
// delivery. Nothing here judges: every status and sentence is the API's, only
// gathered.

export type Tone = "bad" | "warn" | "quiet" | "good";

export type CaseKind =
  | "rejected"
  | "missing"
  | "double_submission"
  | "conflict"
  | "ambiguous_group"
  | "ambiguous"
  | "unmatched"
  | "waiting"
  | "accepted";

export interface KindInfo {
  label: string;
  /** A mark that says it without colour. */
  mark: string;
  tone: Tone;
  /** What the operator does about it (README, "Deliveries: did the bank get it?"). */
  action: string | null;
}

/** Worst first: the order cases are listed in and the summary counts them in. */
export const KINDS: Record<CaseKind, KindInfo> = {
  rejected: { label: "Rejected", mark: "✗", tone: "bad", action: "Correct and resend." },
  missing: { label: "Receipt missing", mark: "!", tone: "bad", action: "Call Nets (+45 80 81 07 07, weekdays 9–16). Do not upload again until they answer." },
  double_submission: { label: "Possible double", mark: "⧉", tone: "bad", action: "Ask Nets whether it was processed twice." },
  conflict: { label: "Conflicting receipts", mark: "≠", tone: "bad", action: "Compare the files by hand; ask Nets if in doubt." },
  ambiguous_group: { label: "Ambiguous", mark: "?", tone: "warn", action: "Compare by hand; ask Nets if in doubt." },
  ambiguous: { label: "Ambiguous", mark: "?", tone: "warn", action: "Compare by hand; ask Nets if in doubt." },
  unmatched: { label: "Receipt without a delivery", mark: "◌", tone: "warn", action: "Was it uploaded outside this service, or for a delivery older than the window?" },
  waiting: { label: "Waiting", mark: "…", tone: "quiet", action: null },
  accepted: { label: "Accepted", mark: "✓", tone: "good", action: null },
};
const ORDER = Object.keys(KINDS) as CaseKind[];

export interface Case {
  key: string;
  kind: CaseKind;
  /** Newest upload first. */
  deliveries: DeliveryRow[];
  /** Every set of receipts in the case, newest first — the deliveries' order, so their times can be compared by eye. */
  answers: Answer[];
  /** Receipts in the case that no delivery is paired with. */
  unpaired: UnpairedAnswer[];
  /** The one sentence: the API's own where there is one, gathered where there are several. */
  sentence: string;
  /** The newest upload, or the receipts' time where there is no delivery. RFC 3339. */
  at: string;
}

export const answerKey = (answer: Answer): string => answer.files.map((f) => f.name).join("|");

/** Every set of receipts a row names, paired first. */
const answersOf = (row: DeliveryRow): Answer[] => [row.answer, ...row.extra, ...row.candidates].filter((a): a is Answer => a !== null);

/** Groups the board into cases, worst first, then newest first. */
export function casesOf(board: DeliveryBoard): Case[] {
  // Union-find over deliveries ("d:") and sets of receipts ("a:").
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    const p = parent.get(x) ?? x;
    if (p === x) return x;
    const root = find(p);
    parent.set(x, root);
    return root;
  };
  const union = (a: string, b: string): void => {
    parent.set(find(a), find(b));
  };
  const answers = new Map<string, Answer>();
  for (const row of board.deliveries) {
    find(`d:${row.name}`);
    for (const answer of answersOf(row)) {
      answers.set(answerKey(answer), answer);
      union(`a:${answerKey(answer)}`, `d:${row.name}`);
    }
  }
  const shown = new Set(board.deliveries.map((row) => row.name));
  for (const u of board.unpaired) {
    answers.set(answerKey(u.answer), u.answer);
    find(`a:${answerKey(u.answer)}`);
    // A candidate outside the window is not on the board: the receipt stands alone.
    for (const name of u.candidates) if (shown.has(name)) union(`a:${answerKey(u.answer)}`, `d:${name}`);
  }

  const groups = new Map<string, { deliveries: DeliveryRow[]; answers: Answer[]; unpaired: UnpairedAnswer[] }>();
  const group = (node: string) => {
    const root = find(node);
    const found = groups.get(root) ?? { deliveries: [], answers: [], unpaired: [] };
    groups.set(root, found);
    return found;
  };
  for (const row of board.deliveries) group(`d:${row.name}`).deliveries.push(row);
  for (const [key, answer] of answers) group(`a:${key}`).answers.push(answer);
  for (const u of board.unpaired) group(`a:${answerKey(u.answer)}`).unpaired.push(u);

  const cases = [...groups.entries()].map(([key, g]): Case => {
    g.answers.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
    const kind = kindOf(g.deliveries, g.answers, g.unpaired);
    const at = g.deliveries[0]?.uploadedAt ?? g.answers[0]?.at ?? "";
    return { key, kind, ...g, at, sentence: sentenceOf(kind, g.deliveries, g.answers, g.unpaired) };
  });
  return cases.sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind) || Date.parse(b.at) - Date.parse(a.at));
}

function kindOf(deliveries: DeliveryRow[], answers: Answer[], unpaired: UnpairedAnswer[]): CaseKind {
  const statuses = new Set(deliveries.map((d) => d.status));
  // A rejection no delivery is paired with is the case's own only where there is no delivery to speak for it.
  if (statuses.has("rejected") || (deliveries.length === 0 && unpaired.some((u) => u.answer.rejected))) return "rejected";
  if (statuses.has("missing")) return "missing";
  if (statuses.has("double_submission")) return "double_submission";
  if (answers.some((a) => a.conflict !== null)) return "conflict";
  if (statuses.has("ambiguous")) return deliveries.length > 1 ? "ambiguous_group" : "ambiguous";
  if (deliveries.length === 0) return unpaired.some((u) => u.status === "ambiguous") ? "ambiguous" : "unmatched";
  if (statuses.has("waiting")) return "waiting";
  return "accepted";
}

const sameFigures = (rows: DeliveryRow[]): boolean =>
  rows.every((row) => row.records === rows[0]?.records && row.items === rows[0]?.items && row.totalOere === rows[0]?.totalOere);

/**
 * The case in one short sentence: what the operator needs to act, no more —
 * the receipts and their numbers are on the line itself, and the API's full
 * reason is under "files".
 */
function sentenceOf(kind: CaseKind, deliveries: DeliveryRow[], answers: Answer[], unpaired: UnpairedAnswer[]): string {
  const [first] = deliveries;
  const reason = first?.reason ?? unpaired[0]?.reason ?? "";
  switch (kind) {
    case "rejected":
      return first?.answer?.files.some((f) => f.kind === "gateway_error") ? "Nets' gateway refused the file before it became a delivery." : reason;
    case "missing":
      // A receipt in its window that cannot be tied to it is on the line, with a "?".
      return unpaired.length > 0 && first?.answer === null ? `No validation receipt (V). A transmission receipt in its window cannot be tied to it.` : reason;
    case "double_submission":
      return `${answers.length} sets of receipts fit this delivery and no other: it may have reached Nets ${answers.length === 2 ? "twice" : `${answers.length} times`}.`;
    case "conflict": {
      const number = answers.find((a) => a.conflict !== null)?.deliveryNumber;
      return `The receipt files for ${number ? `Nets delivery ${number}` : "this delivery"} disagree, so it is not counted as accepted.`;
    }
    case "ambiguous_group": {
      const accepted = answers.every((a) => !a.rejected && a.conflict === null) ? "accepted " : "";
      const alike = sameFigures(deliveries) ? " with the same figures" : "";
      return `${deliveries.length} deliveries${alike}, ${answers.length} ${accepted}receipts that fit them all: which is whose cannot be told.`;
    }
    case "unmatched":
      return answers.some((a) => a.files.some((f) => f.kind === "validation")) ? "Fits none of our deliveries in the window." : reason;
    default:
      return reason;
  }
}

// ── Saying it short ──────────────────────────────────────────────────────────

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;
export { plural };

/** The creditor's PBS number, without the debtor group (shown on hover). */
export function creditorOf(row: DeliveryRow): { short: string; full: string } {
  const [first, ...rest] = row.creditors;
  if (!first) return { short: "—", full: "" };
  const full = row.creditors.map((c) => (c.debtorGroupNumber ? `${c.pbsNumber} / debtor group ${Number(c.debtorGroupNumber)}` : c.pbsNumber)).join(", ");
  return { short: rest.length > 0 ? `${first.pbsNumber} +${rest.length}` : first.pbsNumber, full };
}

const timeOnly = new Intl.DateTimeFormat("da-DK", { hour: "2-digit", minute: "2-digit" });
const dayOnly = new Intl.DateTimeFormat("da-DK", { day: "2-digit", month: "2-digit" });
const weekday = new Intl.DateTimeFormat("en-GB", { weekday: "short" });

export const hhmm = (iso: string): string => timeOnly.format(new Date(iso));
const ddmm = (iso: string): string => dayOnly.format(new Date(iso));
export const dayLabel = (iso: string): string => `${weekday.format(new Date(iso))} ${ddmm(iso)}`;
export const dayKey = (iso: string): string => new Date(iso).toLocaleDateString("sv-SE");

/** "12 min ago", "5 h ago", "3 days ago". */
export function ago(iso: string, now: number): string {
  const minutes = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
  if (minutes < 90) return `${minutes} min ago`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} h ago`;
  return `${Math.round(minutes / 1440)} days ago`;
}

export interface ReceiptState {
  mark: string;
  word: string;
  tone: Tone;
}

/** What a set of receipts says, in a word: accepted, rejected and by which file, only a T, refused, conflicting. */
export function receiptState(answer: Answer): ReceiptState {
  const t = answer.files.find((f) => f.kind === "transmission");
  const v = answer.files.find((f) => f.kind === "validation");
  const gateway = answer.files.find((f) => f.kind === "gateway_error");
  if (answer.conflict) return { mark: "≠", word: "Conflicting files", tone: "bad" };
  if (gateway) return { mark: "✗", word: "Gateway refused", tone: "bad" };
  if (t && !t.positive) return { mark: "✗", word: `T rejected ${t.code ?? ""}`.trim(), tone: "bad" };
  if (v && !v.positive) return { mark: "✗", word: `V rejected ${v.code ?? ""}`.trim(), tone: "bad" };
  if (t && v) return { mark: "✓", word: "Accepted", tone: "good" };
  if (t) return { mark: "…", word: "T only, no V", tone: "quiet" };
  return { mark: "…", word: "V only, no T", tone: "quiet" };
}

/** The bank's counts of a set of receipts: "2 collections · 1.250,00 kr". */
export function answerFigures(answer: Answer): string {
  if (answer.records === null) return "no counts";
  if (answer.amountLines === null) return plural(answer.records, "record");
  return [plural(answer.amountLines, "collection"), answer.totalOere === null ? null : formatAmount(answer.totalOere)].filter(Boolean).join(" · ");
}

export interface Tally {
  kind: CaseKind;
  cases: number;
  deliveries: number;
}

/** Cases per kind, worst first, only the kinds that occur. */
export function tallies(cases: Case[]): Tally[] {
  return ORDER.map((kind) => {
    const of = cases.filter((c) => c.kind === kind);
    return { kind, cases: of.length, deliveries: of.reduce((n, c) => n + c.deliveries.length, 0) };
  }).filter((t) => t.cases > 0);
}

export const isProblem = (c: Case): boolean => KINDS[c.kind].tone === "bad" || KINDS[c.kind].tone === "warn";

/** How a tally reads in the headline: "2 rejected", "1 ambiguous group (3 deliveries)". */
export function tallyText(t: Tally): string {
  switch (t.kind) {
    case "rejected":
      return `${t.cases} rejected`;
    case "missing":
      return `${t.cases} receipt${t.cases === 1 ? "" : "s"} missing`;
    case "double_submission":
      return `${t.cases} possible double${t.cases === 1 ? "" : "s"}`;
    case "conflict":
      return `${t.cases} receipt conflict${t.cases === 1 ? "" : "s"}`;
    case "ambiguous_group":
      return `${t.cases} ambiguous group${t.cases === 1 ? "" : "s"} (${plural(t.deliveries, "delivery", "deliveries")})`;
    case "ambiguous":
      return `${t.cases} ambiguous`;
    case "unmatched":
      return `${t.cases} receipt${t.cases === 1 ? "" : "s"} without a delivery`;
    case "waiting":
      return `${t.deliveries} waiting`;
    case "accepted":
      return `${t.deliveries} accepted`;
  }
}
